import React from "react";
import {
  CurrencyDollarIcon,
  UserGroupIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { Card } from "@/components/Card/Card";
import { MPDashboardSummary as MPDashboardSummaryType } from "../../api/getMPDashboard/types";

interface MPDashboardSummaryProps {
  summary: MPDashboardSummaryType;
  loading: boolean;
  totalZipCount: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

const MPDashboardSummary: React.FC<MPDashboardSummaryProps> = ({
  summary,
  loading,
  totalZipCount,
}) => {
  const stats = [
    {
      label: "Total Sales",
      value: formatCurrency(summary.totalSales),
      icon: CurrencyDollarIcon,
      description: "Across all ZIP codes",
    },
    {
      label: "Total Customers",
      value: formatNumber(summary.totalCustomers),
      icon: UserGroupIcon,
      description: "Total Customers",
    },
    {
      label: "Qualified ZIP Codes",
      value: formatNumber(summary.qualifiedZipCount),
      icon: MapPinIcon,
      description: `Out of ${formatNumber(totalZipCount)} total`,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {stats.map((stat) => (
        <Card
          className="w-full bg-white dark:bg-gray-800 dark:border-white/20"
          key={stat.label}
        >
          <div className="p-5">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <stat.icon className="h-4 w-4" />
              <span className="text-sm">{stat.label}</span>
            </div>
            {loading ? (
              <div className="mt-3 h-8 w-28 animate-pulse rounded bg-gray-200 dark:bg-slate-700" />
            ) : (
              <>
                <div className="mt-3 text-2xl font-bold text-gray-900 dark:text-white">
                  {stat.value}
                </div>
                <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {stat.description}
                </div>
              </>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
};

export default MPDashboardSummary;
